import { createSignal, createMemo, onCleanup, onMount, Show, For } from 'solid-js';
import { useApi, isOk } from '../api';
import { useToast } from '../components/MessageBox';
import { showConfirm } from '../components/Dialog';
import { useTranslation } from '../hooks/useTranslation';
import { SearchInput } from '../components/SearchInput';
import ItemStackView from '../components/ItemStackView';
import ItemSlotGrid, { type ItemSlotGridEntry } from '../components/inventory/ItemSlotGrid';
import LazyRender from '../components/LazyRender';
import { fromMaterialItem } from '../services/itemRefAdapters';
import MinecraftText from '../components/MinecraftText';
import Text from '../components/Text';
import { Citybuild } from '../models/Citybuild';
import { getEnchantmentName as getEnchantmentNameLang } from '../services/enchantmentNames';
import { Skeleton, SkeletonSlotGrid } from '../components/Skeleton';
import { RelativeTime } from '../components/RelativeTime';
import styles from './ShopStock.module.css';
import { firstError } from '../api/envelope';

const getCitybuildLabel = (cb: number): string => {
    const key = Citybuild[cb];
    return key ?? '-';
};

interface ShopStockPrice { amount: number; price: number; }
interface ShopStockEnchantment { id: number; level: number; }
interface ShopStockLore { lineNumber: number; lineContent: string; }
interface ShopStockCatalogueItem {
    id: string;
    displayName: string;
    materialName: string;
    materialSubId: number;
    stackSize: number;
    repairCost: number;
    itemFlags: number;
    enchantmentsJson: string;
    loreJson: string;
}
interface ShopStockStock {
    itemId: string;
    warehouseCount: number;
    isAvailable: boolean;
    prices: ShopStockPrice[];
}
interface ShopStockShop {
    id: string;
    uuid: string;
    name: string;
    citybuild: number;
    fetchedAt: string;
    stock: ShopStockStock[];
}
interface ShopStockSnapshot {
    items: ShopStockCatalogueItem[];
    bots: ShopStockShop[];
}
interface CompareVariant {
    itemId: string;
    enchantmentsJson: string;
    loreJson: string;
    repairCost: number;
    itemFlags: number;
    stackSize: number;
    warehouseCount: number;
    isAvailable: boolean;
    prices: ShopStockPrice[];
}
interface CompareBotEntry {
    botId: string;
    botName: string;
    citybuild: number;
    variants: CompareVariant[];
}
interface CompareData {
    displayName: string;
    materialName: string;
    materialSubId: number;
    bots: CompareBotEntry[];
}

type ShopListing = ShopStockCatalogueItem & ShopStockStock;

const GRID_COLUMNS = 9;
const GRID_ROW_HEIGHT = 72;

export default function ShopStock() {
    const api = useApi();
    const toast = useToast();
    const { t, langCode } = useTranslation();
    const getEnchantmentName = (id: number) => getEnchantmentNameLang(id, langCode());

    const [snapshot, setSnapshot] = createSignal<ShopStockSnapshot>({ items: [], bots: [] });
    const [newUsername, setNewUsername] = createSignal('');
    const [loading, setLoading] = createSignal(false);
    const [initialLoading, setInitialLoading] = createSignal(true);
    const [searchText, setSearchText] = createSignal('');
    const [search, setSearch] = createSignal('');
    const [selectedItemKey, setSelectedItemKey] = createSignal<string | null>(null);
    const [compact, setCompact] = createSignal(false);
    const [showAddForm, setShowAddForm] = createSignal(false);
    const [citybuildFilter, setCitybuildFilter] = createSignal<number | null>(null);
    const [compare, setCompare] = createSignal<CompareData | null>(null);
    const [refetchingBot, setRefetchingBot] = createSignal<string | null>(null);

    // Typing is cheap and re-filtering thousands of listings is not, so the box updates on every
    // keystroke while the filter settles once the user pauses.
    let searchTimer: number | undefined;
    const onSearchInput = (next: string) => {
        setSearchText(next);
        window.clearTimeout(searchTimer);
        searchTimer = window.setTimeout(() => setSearch(next), 150);
    };
    onCleanup(() => window.clearTimeout(searchTimer));

    const stripColors = (s: string) => s.replace(/§[0-9a-fk-or]/gi, '');

    const itemsById = createMemo(() => new Map(snapshot().items.map(item => [item.id, item])));

    // Built once per catalogue rather than once per listing: the same item is stocked by many shops,
    // and colour-stripping plus lower-casing every shop's copy on every keystroke was the search cost.
    const haystackById = createMemo(() => new Map(snapshot().items.map(item =>
        [item.id, `${stripColors(item.displayName)} ${item.materialName}`.toLowerCase()])));

    const visibleBots = createMemo(() => {
        const cb = citybuildFilter();
        return cb === null ? snapshot().bots : snapshot().bots.filter(b => b.citybuild === cb);
    });

    const selectedItem = createMemo(() => {
        const key = selectedItemKey();
        if (!key) return null;
        const [botId, itemId] = key.split(':');
        const bot = snapshot().bots.find(b => b.id === botId);
        const stock = bot?.stock.find(s => s.itemId === itemId);
        const item = itemsById().get(itemId);
        if (!bot || !stock || !item) return null;
        return { bot, listing: { ...item, ...stock } as ShopListing };
    });

    // What the item's own NBT cannot say: the shop's stock, what a stack is worth there, and whether it
    // is currently selling. ItemStackView draws the name, lore, enchantments, repair cost and item id.
    const shopTooltip = (shop: { stackSize: number; warehouseCount: number; isAvailable: boolean; prices: ShopStockPrice[] }) => {
        const sellPrices = [...shop.prices.filter(p => p.price > 0)].sort((a, b) => a.amount - b.amount);
        const buyPrices = [...shop.prices.filter(p => p.price < 0)].sort((a, b) => a.amount - b.amount);

        return (
            <>
                <div class={styles.tooltipRow}>
                    <span class={styles.tooltipLabel}>{t('SHOP_STOCK_STOCK')}:</span>
                    <span>{shop.warehouseCount}</span>
                </div>
                <div class={styles.tooltipRow}>
                    <span class={styles.tooltipLabel}>{t('SHOP_STOCK_STACK')}:</span>
                    <span>{shop.stackSize}</span>
                </div>
                <div class={styles.tooltipRow}>
                    <span class={styles.tooltipLabel}>{t('SHOP_STOCK_STATUS')}:</span>
                    <span class={shop.isAvailable ? styles.available : styles.unavailable}>
                        {shop.isAvailable ? t('SHOP_STOCK_AVAILABLE') : t('SHOP_STOCK_UNAVAILABLE')}
                    </span>
                </div>

                <Show when={sellPrices.length > 0}>
                    <div class={styles.tooltipDivider} />
                    <div class={styles.tooltipSectionLabel}>{t('SHOP_STOCK_SELL_PRICE')}</div>
                    <For each={sellPrices}>
                        {(p) => (
                            <div class={styles.tooltipPriceRow}>
                                <span class={styles.tooltipPriceAmount}>{p.amount}x</span>
                                <span class={styles.priceSell}>{formatPrice(p.price)}</span>
                                <span class={styles.tooltipPerItem}>({formatPrice(p.price / p.amount)}/{t('SHOP_STOCK_PER_ITEM')})</span>
                            </div>
                        )}
                    </For>
                </Show>

                <Show when={buyPrices.length > 0}>
                    <div class={styles.tooltipDivider} />
                    <div class={styles.tooltipSectionLabel}>{t('SHOP_STOCK_BUY_PRICE')}</div>
                    <For each={buyPrices}>
                        {(p) => {
                            const perItem = Math.abs(p.price) / p.amount;
                            return (
                                <div class={styles.tooltipPriceRow}>
                                    <span class={styles.tooltipPriceAmount}>{p.amount}x</span>
                                    <span class={styles.priceBuy}>{formatPrice(Math.abs(p.price))}</span>
                                    <span class={styles.tooltipPerItem}>({formatPrice(perItem)}/{t('SHOP_STOCK_PER_ITEM')} · {formatPrice(perItem * shop.stackSize)}/{t('SHOP_STOCK_PER_STACK')})</span>
                                </div>
                            );
                        }}
                    </For>
                </Show>
            </>
        );
    };

    const fetchSnapshot = async () => {
        const res = await api.shopStock.getSnapshot();
        if (!isOk(res.data)) return;
        setSnapshot(res.data.value as unknown as ShopStockSnapshot);
    };

    onMount(async () => {
        try {
            await fetchSnapshot();
        } finally {
            setInitialLoading(false);
        }
    });

    const addBot = async (e: Event) => {
        e.preventDefault();
        if (!newUsername().trim()) return;
        setLoading(true);
        const res = await api.shopStock.addBot({ username: newUsername().trim() });
        setLoading(false);
        if (isOk(res.data)) {
            setNewUsername('');
            setShowAddForm(false);
            await fetchSnapshot();
            toast?.info(t('SHOP_STOCK_BOT_ADDED'));
        } else {
            toast?.error(firstError(res.data) ?? t('SHOP_STOCK_BOT_ADD_FAILED'));
        }
    };

    const refetchBot = async (id: string) => {
        setRefetchingBot(id);
        const res = await api.shopStock.refetchBot(id);
        setRefetchingBot(null);
        if (isOk(res.data)) {
            await fetchSnapshot();
            toast?.info(t('SHOP_STOCK_BOT_REFETCHED'));
        } else {
            toast?.error(firstError(res.data) ?? t('SHOP_STOCK_BOT_REFETCH_FAILED'));
        }
    };

    const deleteBot = async (id: string, name: string) => {
        if (!await showConfirm(`${t('SHOP_STOCK_REMOVE_CONFIRM')} "${name}"?`, true)) return;
        const res = await api.shopStock.deleteBot(id);
        if (!isOk(res.data)) {
            toast?.error(firstError(res.data) ?? t('SHOP_STOCK_BOT_REMOVE_FAILED'));
            return;
        }
        await fetchSnapshot();
    };

    const updateCitybuild = async (botId: string, citybuild: number) => {
        const res = await api.shopStock.updateBot(botId, { citybuild });
        if (!isOk(res.data)) {
            toast?.error(firstError(res.data) ?? t('SHOP_STOCK_CITYBUILD_FAILED'));
            return;
        }
        setSnapshot(prev => ({
            ...prev,
            bots: prev.bots.map(b => b.id === botId ? { ...b, citybuild } : b),
        }));
    };

    const compareItem = async (itemId: string) => {
        const res = await api.shopStock.compareItem(itemId);
        if (isOk(res.data)) {
            setCompare(res.data.value as unknown as CompareData);
        } else {
            toast?.error(firstError(res.data) ?? t('SHOP_STOCK_COMPARE_FAILED'));
        }
    };

    const formatPrice = (p: number) =>
        `$${Math.abs(p).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    // <For> diffs tiles by object identity, so a rebuilt entry throws away that tile's icon, tooltip
    // and badge DOM even when nothing about the listing changed. Same contract as InventoryPanel.
    const shopGrid = (bot: ShopStockShop) => {
        const tilesByKey = new Map<string, { signature: string; entry: ItemSlotGridEntry }>();

        const listings = createMemo<ShopListing[]>(() => {
            const query = search().toLowerCase();
            const catalogue = itemsById();
            const haystacks = haystackById();

            const matches: ShopListing[] = [];
            for (const stock of bot.stock) {
                const item = catalogue.get(stock.itemId);
                if (!item) continue;
                if (query) {
                    if (!haystacks.get(stock.itemId)?.includes(query)) continue;
                } else if (stock.warehouseCount <= 0) {
                    continue;
                }
                matches.push({ ...item, ...stock });
            }
            return matches;
        });

        const entries = createMemo<ItemSlotGridEntry[]>(() => {
            const selectedKey = selectedItemKey();
            const stillListed = new Set<string>();

            const tiles = listings().map((listing) => {
                stillListed.add(listing.itemId);
                const selected = selectedKey === `${bot.id}:${listing.itemId}`;
                const signature = [
                    listing.warehouseCount, listing.isAvailable, selected,
                    listing.prices.map(p => `${p.amount}@${p.price}`).join(','),
                ].join('|');

                const rendered = tilesByKey.get(listing.itemId);
                if (rendered?.signature === signature) return rendered.entry;

                const entry: ItemSlotGridEntry = {
                    key: listing.itemId,
                    stack: fromMaterialItem(listing),
                    label: stripColors(listing.displayName),
                    selected,
                    extraClass: listing.isAvailable ? '' : styles.slotUnavailable,
                    // A thunk, not the rendered fragment: Solid materialises JSX when it is called, and
                    // a tooltip only ever reaches the document on hover.
                    tooltipExtra: () => shopTooltip(listing),
                    badge: (
                        <>
                            <div class={styles.slotBadge}>{listing.warehouseCount}</div>
                            <Show when={!listing.isAvailable}>
                                <div class={styles.unavailableBadge}>N/A</div>
                            </Show>
                        </>
                    ),
                };
                tilesByKey.set(listing.itemId, { signature, entry });
                return entry;
            });

            for (const key of tilesByKey.keys()) {
                if (!stillListed.has(key)) tilesByKey.delete(key);
            }
            return tiles;
        });

        const estimatedHeight = () => Math.ceil(listings().length / GRID_COLUMNS) * GRID_ROW_HEIGHT;

        return (
            <Show when={entries().length > 0} fallback={
                <div class={styles.emptyItems}>{search() ? t('SHOP_STOCK_NO_ITEMS_SEARCH') : t('SHOP_STOCK_NO_ITEMS')}</div>
            }>
                <LazyRender estimatedHeight={estimatedHeight()}>
                    {() => (
                        <ItemSlotGrid
                            items={entries()}
                            onSelect={(key) => {
                                const selectionKey = `${bot.id}:${key}`;
                                setSelectedItemKey(selectedItemKey() === selectionKey ? null : selectionKey);
                            }}
                        />
                    )}
                </LazyRender>
            </Show>
        );
    };

    return (
        <div class={styles.container}>
            <h1><Text val="SHOP_STOCK_TITLE" /></h1>

            <Show when={showAddForm()}>
                <form onSubmit={addBot} class={`fcard ${styles.addForm}`}>
                    <input
                        type="text"
                        placeholder={t('SHOP_STOCK_USERNAME_PLACEHOLDER')}
                        value={newUsername()}
                        onInput={(e) => setNewUsername(e.target.value)}
                        disabled={loading()}
                    />
                    <button class="btn btn-primary" disabled={loading() || !newUsername().trim()}>
                        {loading() ? t('SHOP_STOCK_ADDING') : t('SHOP_STOCK_ADD_BOT')}
                    </button>
                    <button type="button" class="btn btn-secondary" onClick={() => { setShowAddForm(false); setNewUsername(''); }}>
                        <Text val="CANCEL" />
                    </button>
                </form>
            </Show>

            <div class={styles.toolbar}>
                <SearchInput
                    value={searchText()}
                    onInput={onSearchInput}
                    placeholder={t('SHOP_STOCK_SEARCH_PLACEHOLDER')}
                />
                <select
                    class={styles.filterSelect}
                    value={citybuildFilter() === null ? '' : String(citybuildFilter())}
                    onChange={(e) => {
                        const v = e.currentTarget.value;
                        setCitybuildFilter(v === '' ? null : parseInt(v));
                    }}
                    title={t('SHOP_STOCK_FILTER_CITYBUILD')}
                >
                    <option value="">{t('SHOP_STOCK_ALL_CITYBUILDS')}</option>
                    <For each={Object.keys(Citybuild).filter(key => isNaN(Number(key)))}>
                        {(key) => (
                            <option value={String(Citybuild[key as keyof typeof Citybuild])}>
                                {key}
                            </option>
                        )}
                    </For>
                </select>
                <Show when={snapshot().bots.length > 0}>
                    <button
                        class={`btn btn-secondary ${styles.viewToggle}`}
                        onClick={() => setCompact(c => !c)}
                        title={compact() ? t('SHOP_STOCK_EXPAND_ALL') : t('SHOP_STOCK_COMPACT_ALL')}
                    >
                        <i class={`fa-solid ${compact() ? 'fa-expand' : 'fa-compress'}`} />
                        <span>{compact() ? <Text val="SHOP_STOCK_EXPAND_ALL" /> : <Text val="SHOP_STOCK_COMPACT_ALL" />}</span>
                    </button>
                </Show>
                <button
                    class="btn btn-primary"
                    onClick={() => setShowAddForm(s => !s)}
                >
                    <i class="fa-solid fa-plus" /> <Text val="SHOP_STOCK_ADD_BOT" />
                </button>
            </div>

            <Show when={initialLoading() && snapshot().bots.length === 0}>
                <div class={styles.mainColumn}>
                    <For each={[0, 1, 2]}>
                        {() => (
                            <div class={`fcard ${styles.botCard} ${styles.skeletonCard}`}>
                                <div class={styles.botHeader}>
                                    <Skeleton variant="text" width={160} />
                                    <Skeleton variant="text" width={100} />
                                </div>
                                <SkeletonSlotGrid count={12} />
                            </div>
                        )}
                    </For>
                </div>
            </Show>

            <Show when={!initialLoading() || snapshot().bots.length > 0}>
                <div class={styles.layout}>
                    <div class={styles.mainColumn}>
                        <Show when={visibleBots().length > 0} fallback={
                            <div class={`fcard ${styles.emptyState}`}>
                                <i class="fa-solid fa-shop" />
                                <p><Text val="SHOP_STOCK_NO_BOTS" /></p>
                            </div>
                        }>
                            <For each={visibleBots()}>
                                {(bot) => (
                                    <div class={`fcard ${styles.botCard}`}>
                                        <div class={styles.botHeader}>
                                            <div class={styles.botInfo}>
                                                <span class={styles.botName}>{bot.name}</span>
                                                <select
                                                    class={styles.botCitybuildSelect}
                                                    value={bot.citybuild}
                                                    onChange={(e) => updateCitybuild(bot.id, parseInt(e.currentTarget.value))}
                                                >
                                                    <For each={Object.keys(Citybuild).filter(key => isNaN(Number(key)))}>
                                                        {(key) => (
                                                            <option value={Citybuild[key as keyof typeof Citybuild]}>
                                                                {key}
                                                            </option>
                                                        )}
                                                    </For>
                                                </select>
                                                <span class={styles.botUuid}>{bot.uuid}</span>
                                                <span class={styles.botFetched}>
                                                    <Text val="SHOP_STOCK_FETCHED" /> <RelativeTime value={bot.fetchedAt} />
                                                </span>
                                                <span class={styles.botItemCount}>{bot.stock.length} <Text val="SHOP_STOCK_ITEMS" /></span>
                                            </div>
                                            <div class={styles.botActions}>
                                                <button
                                                    class="btn btn-secondary"
                                                    onClick={() => refetchBot(bot.id)}
                                                    disabled={refetchingBot() === bot.id}
                                                >
                                                    <i class={`fa-solid fa-refresh ${refetchingBot() === bot.id ? 'fa-spin' : ''}`} />
                                                    {refetchingBot() === bot.id ? ` ${t('SHOP_STOCK_FETCHING')}` : ` ${t('SHOP_STOCK_REFETCH')}`}
                                                </button>
                                                <button class="btn btn-danger" onClick={() => deleteBot(bot.id, bot.name)}>
                                                    <i class="fa-solid fa-trash" />
                                                </button>
                                            </div>
                                        </div>

                                        <Show when={!compact()}>
                                            {shopGrid(bot)}
                                        </Show>
                                    </div>
                                )}
                            </For>
                        </Show>
                    </div>

                    <Show when={!selectedItem() && snapshot().bots.length > 0}>
                        <div class={styles.detailPanel}>
                            <div class={styles.detailEmpty}>
                                <i class="fa-solid fa-arrow-pointer" />
                                <Text val="SHOP_STOCK_SELECT_ITEM" />
                            </div>
                        </div>
                    </Show>
                    <Show when={selectedItem()} keyed>
                        {(_sel) => {
                            const item = () => selectedItem()!.listing;
                            const bot = () => selectedItem()!.bot;
                            const buyPrices = () => [...item().prices.filter(p => p.price < 0)].sort((a, b) => a.amount - b.amount);
                            const sellPrices = () => [...item().prices.filter(p => p.price > 0)].sort((a, b) => a.amount - b.amount);
                            return (
                                <div class={styles.detailPanel}>
                                    <div class={styles.detailHeader}>
                                        <ItemStackView stack={fromMaterialItem(item())} size={48} />
                                        <div>
                                            <div class={styles.detailName}><MinecraftText text={item().displayName} /></div>
                                            <div class={styles.detailMaterial}>{item().materialName}:{item().materialSubId}</div>
                                        </div>
                                        <button class={styles.closeBtn} onClick={() => setSelectedItemKey(null)}>
                                            <i class="fa-solid fa-xmark" />
                                        </button>
                                    </div>

                                    <div class={styles.detailRow}>
                                        <span class={styles.detailLabel}><Text val="SHOP_STOCK_STOCK" /></span>
                                        <span>{item().warehouseCount}</span>
                                    </div>
                                    <div class={styles.detailRow}>
                                        <span class={styles.detailLabel}><Text val="SHOP_STOCK_STACK" /></span>
                                        <span>{item().stackSize}</span>
                                    </div>
                                    <div class={styles.detailRow}>
                                        <span class={styles.detailLabel}><Text val="SHOP_STOCK_STATUS" /></span>
                                        <span class={item().isAvailable ? styles.available : styles.unavailable}>
                                            {item().isAvailable ? t('SHOP_STOCK_AVAILABLE') : t('SHOP_STOCK_UNAVAILABLE')}
                                        </span>
                                    </div>
                                    <div class={styles.detailRow}>
                                        <span class={styles.detailLabel}><Text val="SHOP_STOCK_BOT" /></span>
                                        <span>{bot().name}</span>
                                    </div>
                                    <div class={styles.detailRow}>
                                        <span class={styles.detailLabel}><Text val="SHOP_STOCK_REPAIR_COST" /></span>
                                        <span>{item().repairCost}</span>
                                    </div>

                                    <Show when={(() => { try { const e = JSON.parse(item().enchantmentsJson) as ShopStockEnchantment[]; return e.length > 0 ? e : null; } catch { return null; } })()}>
                                        {(enchants) => (
                                            <div class={styles.detailSection}>
                                                <div class={styles.detailSectionTitle}><Text val="SHOP_STOCK_ENCHANTMENTS" /></div>
                                                <For each={enchants()}>
                                                    {(e) => (
                                                        <div class={styles.detailEnchant}>
                                                            <span class={styles.enchantName}>{getEnchantmentName(e.id)}</span>
                                                            <span class={styles.enchantLevel}>{e.level}</span>
                                                        </div>
                                                    )}
                                                </For>
                                            </div>
                                        )}
                                    </Show>

                                    <Show when={(() => { try { const l = JSON.parse(item().loreJson) as ShopStockLore[]; return l.length > 0 ? l : null; } catch { return null; } })()}>
                                        {(lore) => (
                                            <div class={styles.detailSection}>
                                                <div class={styles.detailSectionTitle}><Text val="SHOP_STOCK_LORE" /></div>
                                                <div class={styles.loreBlock}>
                                                    <For each={lore()}>
                                                        {(line) => (
                                                            <div class={styles.loreLine}><MinecraftText text={line.lineContent} /></div>
                                                        )}
                                                    </For>
                                                </div>
                                            </div>
                                        )}
                                    </Show>

                                    <Show when={sellPrices().length > 0}>
                                        <div class={styles.detailSection}>
                                            <div class={styles.detailSectionTitle}><Text val="SHOP_STOCK_SELL_PRICE" /></div>
                                            <table class={styles.priceTable}>
                                                <thead><tr><th><Text val="SHOP_STOCK_AMOUNT" /></th><th><Text val="SHOP_STOCK_PRICE" /></th><th>{t('SHOP_STOCK_PER_ITEM')}</th></tr></thead>
                                                <tbody>
                                                    <For each={sellPrices()}>
                                                        {(p) => (
                                                            <tr class={styles.priceSell}>
                                                                <td>{p.amount}x</td>
                                                                <td>{formatPrice(p.price)}</td>
                                                                <td>{formatPrice(p.price / p.amount)}</td>
                                                            </tr>
                                                        )}
                                                    </For>
                                                </tbody>
                                            </table>
                                        </div>
                                    </Show>

                                    <Show when={buyPrices().length > 0}>
                                        <div class={styles.detailSection}>
                                            <div class={styles.detailSectionTitle}><Text val="SHOP_STOCK_BUY_PRICE" /></div>
                                            <table class={styles.priceTable}>
                                                <thead><tr><th><Text val="SHOP_STOCK_AMOUNT" /></th><th><Text val="SHOP_STOCK_PRICE" /></th><th>{t('SHOP_STOCK_PER_ITEM')}</th><th>{t('SHOP_STOCK_PER_STACK')}</th></tr></thead>
                                                <tbody>
                                                    <For each={buyPrices()}>
                                                        {(p) => {
                                                            const perItem = Math.abs(p.price) / p.amount;
                                                            return (
                                                                <tr class={styles.priceBuy}>
                                                                    <td>{p.amount}x</td>
                                                                    <td>{formatPrice(Math.abs(p.price))}</td>
                                                                    <td>{formatPrice(perItem)}</td>
                                                                    <td>{formatPrice(perItem * item().stackSize)}</td>
                                                                </tr>
                                                            );
                                                        }}
                                                    </For>
                                                </tbody>
                                            </table>
                                        </div>
                                    </Show>

                                    <button
                                        class="btn btn-secondary"
                                        style={{ width: '100%', "margin-top": 'var(--spacing-md)' }}
                                        onClick={() => compareItem(item().id)}
                                    >
                                        <i class="fa-solid fa-code-compare" /> <Text val="SHOP_STOCK_COMPARE" />
                                    </button>
                                </div>
                            );
                        }}
                    </Show>
                </div>
            </Show>

            <Show when={compare()}>
                <div class={styles.compareOverlay} onClick={() => setCompare(null)}>
                    <div class={styles.compareModal} onClick={(e) => e.stopPropagation()}>
                        <div class={styles.compareHeader}>
                            <ItemStackView stack={fromMaterialItem(compare()!)} size={48} />
                            <div>
                                <h2><MinecraftText text={compare()!.displayName} /></h2>
                                <div class={styles.detailMaterial}>{compare()!.materialName}:{compare()!.materialSubId}</div>
                            </div>
                            <button class={styles.closeBtn} onClick={() => setCompare(null)}>
                                <i class="fa-solid fa-xmark" />
                            </button>
                        </div>

                        <For each={compare()!.bots}>
                            {(bot) => (
                                <div class={styles.compareBotCard}>
                                    <div class={styles.compareBotHeader}>
                                        <span class={styles.compareBotName}>{bot.botName}</span>
                                        <span class={styles.compareBotCb}>{getCitybuildLabel(bot.citybuild)}</span>
                                    </div>

                                    <For each={bot.variants}>
                                        {(v) => {
                                            const sell = () => [...v.prices.filter((p: ShopStockPrice) => p.price > 0)].sort((a, b) => a.amount - b.amount);
                                            const buy = () => [...v.prices.filter((p: ShopStockPrice) => p.price < 0)].sort((a, b) => a.amount - b.amount);
                                            const enchants = (): ShopStockEnchantment[] => { try { return JSON.parse(v.enchantmentsJson); } catch { return []; } };
                                            return (
                                                <div class={`${styles.compareVariant} ${!v.isAvailable ? styles.rowUnavailable : ''}`}>
                                                    <div class={styles.compareVariantHeader}>
                                                        <ItemStackView
                                                            stack={fromMaterialItem({
                                                                materialName: compare()!.materialName,
                                                                materialSubId: compare()!.materialSubId,
                                                                enchantmentsJson: v.enchantmentsJson,
                                                                loreJson: v.loreJson,
                                                                displayName: compare()!.displayName,
                                                                repairCost: v.repairCost,
                                                            })}
                                                            size={32}
                                                            bare
                                                            showTooltip
                                                            tooltipExtra={() => shopTooltip(v)}
                                                        />
                                                        <span><Text val="SHOP_STOCK_STOCK" />: {v.warehouseCount}</span>
                                                        <span class={v.isAvailable ? styles.available : styles.unavailable}>
                                                            {v.isAvailable ? t('SHOP_STOCK_AVAILABLE') : t('SHOP_STOCK_UNAVAILABLE')}
                                                        </span>
                                                        <Show when={enchants().length > 0}>
                                                            <span class={styles.compareEnchantBadge}>{t('SHOP_STOCK_ENCHANT_COUNT', enchants().length)}</span>
                                                        </Show>
                                                    </div>

                                                    <Show when={sell().length > 0}>
                                                        <div class={styles.detailSection}>
                                                            <div class={styles.detailSectionTitle}><Text val="SHOP_STOCK_SELL_PRICE" /></div>
                                                            <For each={sell()}>
                                                                {(p) => (
                                                                    <div class={`${styles.comparePriceRow} ${styles.priceSell}`}>
                                                                        <span>{p.amount}x</span>
                                                                        <span>{formatPrice(p.price)}</span>
                                                                        <span class={styles.tooltipPerItem}>{formatPrice(p.price / p.amount)}/{t('SHOP_STOCK_PER_ITEM')}</span>
                                                                    </div>
                                                                )}
                                                            </For>
                                                        </div>
                                                    </Show>

                                                    <Show when={buy().length > 0}>
                                                        <div class={styles.detailSection}>
                                                            <div class={styles.detailSectionTitle}><Text val="SHOP_STOCK_BUY_PRICE" /></div>
                                                            <For each={buy()}>
                                                                {(p) => (
                                                                    <div class={`${styles.comparePriceRow} ${styles.priceBuy}`}>
                                                                        <span>{p.amount}x</span>
                                                                        <span>{formatPrice(Math.abs(p.price))}</span>
                                                                        <span class={styles.tooltipPerItem}>{formatPrice(Math.abs(p.price) / p.amount)}/{t('SHOP_STOCK_PER_ITEM')}</span>
                                                                    </div>
                                                                )}
                                                            </For>
                                                        </div>
                                                    </Show>
                                                </div>
                                            );
                                        }}
                                    </For>
                                </div>
                            )}
                        </For>
                    </div>
                </div>
            </Show>
        </div>
    );
}
